import { useOwnershipLabels } from "../../OwnershipLabels";
import { useState } from 'react';
import { createPortal } from 'react-dom';
import Modal from '../../../components/ui/Modal';
import { CLIENT_ENTITY_TYPES, HOLDING_KINDS, TRUST_KINDS, entityTypeComplete, entityTypeLabel } from '../../clientEntityType';
import { BottomSheet } from './KycBottomSheet';
import { useLockBodyScroll } from '../common/common_utils/useLockBodyScroll';

const CARD_ENTITY_TYPES = ['operating', 'holding', 'trust', 'foundation', 'pic', 'quoted']
  .map(value => CLIENT_ENTITY_TYPES.find(option => option.value === value));

function FollowUpOptions({ followUp, draft, disabled, onChoose }) {
  return <div className="kycEntityTypeList is-compact" role="radiogroup" aria-label={followUp.title}>
    {followUp.options.map(option => <button key={option.value} type="button" role="radio" disabled={disabled} aria-label={option.label}
      aria-checked={draft[followUp.key] === option.value}
      className={`kycEntityTypeOption${draft[followUp.key] === option.value ? ' is-selected' : ''}`}
      onClick={() => onChoose(option.value)}>
      <span><strong>{option.label}</strong><small>{option.hint}</small></span>
    </button>)}
  </div>;
}

// The holding / trust question in its own centred modal (animated in and out); closing it leaves the type unchosen.
// lockScroll: false when opened over a sheet or modal that already locks the page.
export function FollowUpModal({ followUp, draft, disabled, onChoose, onClose, lockScroll }) {
  useLockBodyScroll(!!followUp && lockScroll);
  return createPortal(<Modal open={!!followUp} title={followUp?.title || ''} className="kycEntityCountryModal kycOrgPickerModal" onClose={onClose}>
    {followUp && <FollowUpOptions followUp={followUp} draft={draft} disabled={disabled} onChoose={onChoose} />}
  </Modal>, document.body);
}

// The type, then its follow-up question (holding: active or passive; trust: trust or trustee name) in a modal.
// variant "cards" (desktop start card): 2 columns with an icon tile, like the ownership step.
export function EntityTypeOptions({ value, onChange, disabled, variant = 'list', commonBadge = false, lockScroll = true }) {
  const { ownershipLabel } = useOwnershipLabels();
  const [draft, setDraft] = useState(value);
  const choose = next => {
    setDraft(next);
    if (entityTypeComplete(next)) onChange(next);
  };
  const followUp = draft.type === 'holding' ? { title: 'Holding type', options: HOLDING_KINDS, key: 'holdingKind' }
    : draft.type === 'trust' ? { title: 'Client name', options: TRUST_KINDS, key: 'trustKind' } : null;
  return <div className={`kycEntityTypeOptions is-${variant}`}>
    <div className="kycEntityTypeList" role="radiogroup" aria-label="Client entity type">
      {(variant === 'cards' ? CARD_ENTITY_TYPES : CLIENT_ENTITY_TYPES).map(option => <button key={option.value} type="button" role="radio" disabled={disabled} aria-label={ownershipLabel(option.title)}
        aria-checked={draft.type === option.value} className={`kycEntityTypeOption${draft.type === option.value ? ' is-selected' : ''}`}
        onClick={() => choose({ type: option.value, holdingKind: '', trustKind: '' })}>
        <span className="kycEntityTypeIcon" aria-hidden="true"><img src={option.iconSrc} alt="" /></span>
        <span className="kycEntityTypeText">
          <strong>{ownershipLabel(option.title)}{(variant === 'cards' || commonBadge) && option.value === 'operating' && <em className="kycEntityTypeBadge">Most common</em>}</strong>
          <small>{option.hint}</small>
        </span>
      </button>)}
    </div>
    <FollowUpModal followUp={followUp && !draft[followUp.key] ? followUp : null} draft={draft} disabled={disabled} lockScroll={lockScroll}
      onChoose={answer => choose({ ...draft, [followUp.key]: answer })}
      onClose={() => setDraft({ type: '', holdingKind: '', trustKind: '' })} />
  </div>;
}

function ChangePicker({ open, mobile, value, onChange, onClose }) {
  useLockBodyScroll(open && !mobile);
  const content = open && <EntityTypeOptions value={{ ...value, holdingKind: '', trustKind: '' }} lockScroll={false} onChange={next => { onChange(next); onClose(); }} />;
  if (mobile) return <BottomSheet open={open} title="Client entity type" onClose={onClose}>{content}</BottomSheet>;
  return createPortal(<Modal open={open} title="Client entity type" className="kycEntityTypeModal" onClose={onClose}>{content}</Modal>, document.body);
}

/**
 * Client entity type, asked in the company upload-first step (kyc-sent). Same choices and storage as
 * the ownership step (src/kyc/clientEntityType.js). Once answered it reads "Operating company · Change".
 */
export default function KycEntityTypeChoice({ value, onChange, mobile = false, disabled = false, variant = 'list', question = 'Organisation type' }) {
  const { ownershipLabel } = useOwnershipLabels();
  const [changing, setChanging] = useState(false);
  if (!entityTypeComplete(value)) {
    return <div className={`kycEntityTypeAsk is-${variant}`}>
      {/* Desktop cards: the start card's heading asks the question. */}
      {variant !== 'cards' && <p className="kycEntityMobileLabel">{question}</p>}
      <EntityTypeOptions value={value} onChange={onChange} disabled={disabled} variant={variant} commonBadge={mobile} />
    </div>;
  }
  const option = CLIENT_ENTITY_TYPES.find(t => t.value === value.type);
  return <div className="kycEntityTypeRow">
    <img src={option?.iconSrc} alt="" aria-hidden="true" />
    <span>{ownershipLabel(entityTypeLabel(value))}{value.type === 'trust' && <small>{TRUST_KINDS.find(k => k.value === value.trustKind)?.label}</small>}</span>
    <button type="button" className="kycEntityGuideLink" aria-label="Change entity type" disabled={disabled} onClick={() => setChanging(true)}>Change</button>
    <ChangePicker open={changing} mobile={mobile} value={value} onChange={onChange} onClose={() => setChanging(false)} />
  </div>;
}
